import { useRef, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import Turnstile from '@components/utils/turnstile'
import { IconDoc } from '@components/utils/icons'
import * as gtag from '@lib/utilities/analytics'

/**
 * The CV form, restyled to the booking-form card (the pre-redesign version
 * carried S&B-era classes — font-georgia, text-blue, border-light — that no
 * longer exist in this design system, so it rendered half-unstyled).
 * Same stack: kicker → heading → labelled fields → CV picker → Turnstile →
 * full-width submit. The file input stays native but hidden behind a styled
 * row that shows the chosen filename — the browser's own control cannot be
 * styled and read as a broken element in the old layout.
 */
export interface CareerFormProps {
  appointmentHeading: string,
}

interface FormValues {
  Name: string,
  Phone: string,
  Email: string,
  CV: string
}

const FIELD_CLS =
  'w-full rounded-[10px] border-[1.5px] border-rule bg-white px-3.5 py-3 text-ink transition-colors ' +
  'placeholder:text-muted/60 hover:border-violet-400 focus:border-violet-700 focus:outline-none ' +
  'focus:ring-[3px] focus:ring-violet-050'

export default function CareerForm(props: CareerFormProps) {
  const { t } = useTranslation('main')
  const [outcome, setOutcome] = useState('')
  const [hasErrors, setHasErrors] = useState(false)
  const [cvName, setCvName] = useState('')
  const { register, handleSubmit, formState: { errors }, clearErrors } = useForm<FormValues>()

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const cvInputFile = useRef<HTMLInputElement>(null)

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const formData = new FormData()
    // FLAT fields: the API route reads fields.Name/Email/Phone directly —
    // the legacy nested-JSON «data» blob made every submission 422
    formData.append('Name', data.Name)
    formData.append('Phone', data.Phone ?? '')
    formData.append('Email', data.Email ?? '')
    if (turnstileToken) formData.append('turnstileToken', turnstileToken)
    formData.append('files.CV', cvInputFile.current?.files?.item(0) as File)

    fetch('api/career-request', {
      method: 'POST',
      headers: { 'Accept': 'application/json, text/plain, */*' },
      body: formData
    }).then((res) => {
      if (res.status === 200) {
        gtag.event('career_submit', { event_category: 'conversion' })
        setOutcome(t('careers.success'))
        setHasErrors(false)
      } else {
        setOutcome(t('careers.failure'))
        setHasErrors(true)
      }
    })
  }

  const { ref: cvRegisterRef, ...cvRegister } = register('CV')

  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-rule bg-white p-6 shadow-[0_26px_60px_-20px_rgba(23,17,31,0.18)] md:p-8">
      <span className="text-[0.7rem] font-extrabold tracking-[0.09em] text-violet-700">
        {t('careers.kicker')}
      </span>
      <h3 className="text-xl font-extrabold text-ink">{props.appointmentHeading}</h3>
      <p className="text-sm text-muted">{t('careers.note')}</p>

      <form className="flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="Name" className="text-[0.95rem] font-bold text-ink">{t('careers.nameLabel')}</label>
          <input {...register('Name', { required: true })}
            id="Name" name="Name" type="text" autoComplete="name"
            placeholder={t('careers.namePlaceholder')}
            aria-invalid={errors.Name ? 'true' : undefined}
            className={`${FIELD_CLS} ${errors.Name ? 'border-2 border-error' : ''}`} />
          {errors.Name ? <span className="text-xs font-bold text-error">{t('booking.required')}</span> : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="Phone" className="text-[0.95rem] font-bold text-ink">{t('careers.phoneLabel')}</label>
          <input {...register('Phone')}
            id="Phone" name="Phone" type="tel" autoComplete="tel"
            placeholder={t('careers.phonePlaceholder')}
            className={FIELD_CLS} />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="Email" className="text-[0.95rem] font-bold text-ink">{t('careers.emailLabel')}</label>
          <input {...register('Email')}
            id="Email" name="Email" type="email" autoComplete="email"
            placeholder={t('careers.emailPlaceholder')}
            className={FIELD_CLS} />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="CV" className="text-[0.95rem] font-bold text-ink">{t('careers.cvLabel')}</label>
          <button type="button"
            onClick={() => cvInputFile.current?.click()}
            className={`${FIELD_CLS} flex items-center justify-between gap-3 text-left ${errors.CV ? 'border-2 border-error' : ''}`}>
            <span className={cvName ? 'font-bold text-ink' : 'text-muted/60'}>
              {cvName || t('careers.cvPlaceholder')}
            </span>
            <IconDoc width={18} height={18} className="flex-none text-violet-700" aria-hidden />
          </button>
          <input {...cvRegister}
            ref={(el) => { cvRegisterRef(el); cvInputFile.current = el }}
            onChange={(e) => { clearErrors('CV'); setCvName(e.target.files?.item(0)?.name ?? '') }}
            accept="application/pdf"
            id="CV" name="CV" type="file" className="sr-only" tabIndex={-1} />
          <span className="text-xs text-muted">{t('careers.cvHint')}</span>
        </div>

        <Turnstile onToken={setTurnstileToken} />

        <button type="submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet-700 px-6 py-3.5 font-extrabold text-white transition-colors hover:bg-violet-800">
          <IconDoc width={17} height={17} aria-hidden />
          {t('careers.submit')}
        </button>
      </form>

      {outcome ? (
        <span role="status" className={`block text-center text-sm font-bold ${hasErrors ? 'text-error' : 'text-success'}`}>
          {outcome}
        </span>
      ) : null}
    </div>
  )
}
